import React, { useState } from "react";
import { ValidationMessage } from "../UI/ValidationMessage";
import { FileUpload } from "../UI/FileUpload";
import { ClientType } from "../../types/form";
import { isValidClientType } from "../../utils/clientConfig";
import { Popover } from "flowbite-react";
import { SocialMedia } from "./SocialMedia";

interface CustomButton {
  id: number;
  link?: string;
  text?: string;
  linkId: string;
  textId: string;
}

interface AboutMeProps {
  formData: {
    // Basic & Contact merged
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    headshot?: File | string | null;
    businessLogo?: File | string | null;
    wasReferred?: boolean;
    referralName?: string;
    isEditing?: boolean;
    email: string;
    phone: string;
    website?: string;
    address?: {
      streetAddress1?: string;
      streetAddress2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };

    // Existing About Me
    aboutMeText?: string;
    service?: string;
    customButtons?: CustomButton[];
    socialMedia?: {
      instagram?: string;
      facebook?: string;
      linkedin?: string;
      tiktok?: string;
      youtube?: string;
      x?: string;
      whatsapp?: string;
      telegram?: string;
      snapchat?: string;
    };
  };
  onUpdate: (field: string, value: any) => void;
  errors: { [key: string]: string };
  setIsEmailBeingChanged?: (value: boolean) => void;
}


export const AboutMe: React.FC<AboutMeProps> = ({
  formData,
  onUpdate,
  errors,
  setIsEmailBeingChanged,
}) => {
  const [customButtonCount, setCustomButtonCount] = useState(
    formData.customButtons?.length || 0,
  );

  // Initialize with default button if none exist
  React.useEffect(() => {
    if (!formData.customButtons || formData.customButtons.length === 0) {
      const defaultButton: CustomButton = {
        id: 1,
        text: "Book an Appointment",
        link: "https://scheduling-link-example.com",
        linkId: "button1Link",
        textId: "button1",
      };
      onUpdate("customButtons", [defaultButton]);
      setCustomButtonCount(1);
    }
  }, [formData.customButtons, onUpdate]);

  const handleInputChange = (field: string, value: string) => {
    onUpdate(field, value);
  };

  const handleFileSelect = (file: File | null) => {
    onUpdate("headshot", file);
  };

  const handleBusinessLogoSelect = (file: File | null) => {
    onUpdate("businessLogo", file);
  };



  const handleAddressChange = (field: string, value: string) => {
    const currentAddress = formData.address || {};
    const updatedAddress = { ...currentAddress, [field]: value };
    const cleanedAddress = Object.entries(updatedAddress).reduce(
      (acc, [key, val]) => {
        if (val && (val as string).trim() !== "") {
          (acc as any)[key] = val;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
    onUpdate("address", Object.keys(cleanedAddress).length > 0 ? cleanedAddress : {});
  };

  const [clientType, setClientType] = React.useState<ClientType>("Default");
  React.useEffect(() => {
    const param = new URLSearchParams(window.location.search);
    const clientTypeParam = param.get("clientType");
    if (clientTypeParam && isValidClientType(clientTypeParam)) {
      setClientType(clientTypeParam as ClientType);
    }
  }, []);

  const handleCustomButtonChange = (
    buttonId: number,
    field: "link" | "text",
    value: string,
  ) => {
    const currentButtons = formData.customButtons || [];
    const updatedButtons = currentButtons.map((button) =>
      button.id === buttonId ? { ...button, [field]: value } : button,
    );
    onUpdate("customButtons", updatedButtons);
  };

  const addCustomButton = () => {
    const currentButtons = formData.customButtons || [];
    if (currentButtons.length >= 3) return; // Limit to 3 buttons
    
    const newButtonId = customButtonCount + 1;
    const newButton: CustomButton = {
      id: newButtonId,
      link: "",
      text: "",
      linkId: `button${newButtonId}Link`,
      textId: `button${newButtonId}`,
    };

    const updatedButtons = [...currentButtons, newButton];
    onUpdate("customButtons", updatedButtons);
    setCustomButtonCount(newButtonId);
  };

  const removeCustomButton = (buttonId: number) => {
    const currentButtons = formData.customButtons || [];
    const updatedButtons = currentButtons.filter(
      (button) => button.id !== buttonId,
    );
    onUpdate("customButtons", updatedButtons);
  };

  return (
    <div className="form-step active">
      {/* Basic Info section */}
      <div className="form-field required">
        <FileUpload
          label="Please upload a headshot"
          onFileSelect={handleFileSelect}
          currentFile={formData.headshot}
          required={true}
          error={errors.headshot}
        />
      </div>
      <div className="form-field required">
        <label>Your name</label>
        <div className="grid-2">
          <div>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              onBlur={(e) =>
                handleInputChange("firstName", e.target.value.trim())
              }
              placeholder="First Name"
              className={errors.firstName ? "error" : ""}
            />
            {errors.firstName && (
              <ValidationMessage type="error" message={errors.firstName} />
            )}
          </div>
          <div>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              onBlur={(e) =>
                handleInputChange("lastName", e.target.value.trim())
              }
              placeholder="Last Name"
              className={errors.lastName ? "error" : ""}
            />
            {errors.lastName && (
              <ValidationMessage type="error" message={errors.lastName} />
            )}
          </div>
        </div>
      </div>

      {/* Contact Info section */}
      <div className="grid-2">
        <div className="form-field required">
          <label htmlFor="email">Email address</label>
          <input
            disabled={formData?.isEditing}
            title={formData?.isEditing ? "Email cannot be edited" : ""}
            readOnly={formData?.isEditing}
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => {
              handleInputChange("email", e.target.value);
              setIsEmailBeingChanged && setIsEmailBeingChanged(true);
            }}
            onBlur={(e) => {
              handleInputChange("email", e.target.value.trim());
              setIsEmailBeingChanged && setIsEmailBeingChanged(false);
            }}
            placeholder="your.email@example.com"
            className={errors.email ? "error" : ""}
          />
          {errors.email && (
            <ValidationMessage type="error" message={errors.email} />
          )}
        </div>
        <div className="form-field required">
          <label htmlFor="phone">Phone number</label>
          <input
            id="phone"
            type="tel"
            pattern="[0-9\(\)\+\s\-]{8,}"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            onBlur={(e) => handleInputChange("phone", e.target.value.trim())}
            onKeyDown={(e) => {
              if (
                e.ctrlKey ||
                e.metaKey ||
                [
                  "Backspace",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                  "Home",
                  "End",
                ].includes(e.key)
              ) {
                return;
              }
              const allowedChars = /[0-9+\-()\s]/;
              const key = e.key;
              if (key.length === 1 && !allowedChars.test(key)) {
                e.preventDefault();
              }
            }}
            placeholder="(555) 123-4567"
            className={errors.phone ? "error" : ""}
          />
          <small style={{ color: "#666", fontSize: "12px" }}>
            Enter your phone number with or without formatting
          </small>
          {errors.phone && (
            <ValidationMessage type="error" message={errors.phone} />
          )}
        </div>
      </div>

      {/* About Me & Services section */}
      <>
        <div className="form-field">
          <label htmlFor="aboutMeText">
            Provide a short biography of yourself
          </label>
          <textarea
            id="aboutMeText"
            value={formData.aboutMeText || ""}
            rows={5}
            onChange={(e) => handleInputChange("aboutMeText", e.target.value)}
            onBlur={(e) =>
              handleInputChange("aboutMeText", e.target.value.trim())
            }
            placeholder="Tell people about yourself, your experience, and what makes you unique..."
            className={errors.aboutMeText ? "error" : ""}
          />
          {errors.aboutMeText && (
            <ValidationMessage type="error" message={errors.aboutMeText} />
          )}
        </div>

        <div className="form-field">
          <label htmlFor="service">Describe the services you offer</label>
          <textarea
            id="service"
            value={formData.service || ""}
            rows={5}
            onChange={(e) => handleInputChange("service", e.target.value)}
            onBlur={(e) => handleInputChange("service", e.target.value.trim())}
            placeholder="Detail the services or products you provide..."
            className={errors.service ? "error" : ""}
          />
          {errors.service && (
            <ValidationMessage type="error" message={errors.service} />
          )}
        </div>
      </>

      <div className="grid-2">
        <div className="form-field">
          <label htmlFor="title">Job Title</label>
          <input
            id="title"
            type="text"
            value={formData.title || ""}
            onChange={(e) => handleInputChange("title", e.target.value)}
            onBlur={(e) => handleInputChange("title", e.target.value.trim())}
            placeholder="e.g., CEO, Marketing Director"
          />
        </div>
        {clientType !== "Onboarding" && clientType !== "KlinTeam" && (
          <div className="form-field">
            <label htmlFor="organizationName">Business/organization name</label>
            <input
              id="organizationName"
              type="text"
              value={formData.organizationName || ""}
              onChange={(e) =>
                handleInputChange("organizationName", e.target.value)
              }
              onBlur={(e) =>
                handleInputChange("organizationName", e.target.value.trim())
              }
              placeholder="Your company name"
            />
          </div>
        )}
      </div>

      {clientType !== "Onboarding" && clientType !== "KlinTeam" && (
        <div className="form-field">
          <FileUpload
            label="Business logo (optional)"
            onFileSelect={handleBusinessLogoSelect}
            currentFile={formData.businessLogo}
            required={false}
            error={errors.businessLogo}
          />
        </div>
      )}
      <div className="form-field">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          type="url"
          value={formData.website || ""}
          onChange={(e) => handleInputChange("website", e.target.value)}
          onBlur={(e) =>
            handleInputChange(
              "website",
              e.target.value.startsWith("http")
                ? e.target.value
                : e.target.value.length > 4
                  ? `https://${e.target.value}`
                  : ""
            )
          }
          placeholder="example.com or https://example.com"
          className={errors.website ? "error" : ""}
        />
        {errors.website && (
          <ValidationMessage type="error" message={errors.website} />
        )}
      </div>

      {clientType !== "Onboarding" && clientType !== "KlinTeam" && (
        <div className="form-field">
          <label>Business address</label>
          <div className="grid" style={{ gap: "1rem" }}>
            <input
              type="text"
              value={formData.address?.streetAddress1 || ""}
              onChange={(e) =>
                handleAddressChange("streetAddress1", e.target.value)
              }
              onBlur={(e) =>
                handleAddressChange("streetAddress1", e.target.value.trim())
              }
              placeholder="Street Address 1"
            />
            <input
              type="text"
              value={formData.address?.streetAddress2 || ""}
              onChange={(e) =>
                handleAddressChange("streetAddress2", e.target.value)
              }
              onBlur={(e) =>
                handleAddressChange("streetAddress2", e.target.value.trim())
              }
              placeholder="Street Address 2"
            />
            <div className="grid-3">
              <input
                type="text"
                value={formData.address?.city || ""}
                onChange={(e) => handleAddressChange("city", e.target.value)}
                onBlur={(e) =>
                  handleAddressChange("city", e.target.value.trim())
                }
                placeholder="City"
              />
              <input
                type="text"
                value={formData.address?.state || ""}
                onChange={(e) => handleAddressChange("state", e.target.value)}
                onBlur={(e) =>
                  handleAddressChange("state", e.target.value.trim())
                }
                placeholder="State/Province"
              />
              <input
                type="text"
                value={formData.address?.zipCode || ""}
                onChange={(e) => handleAddressChange("zipCode", e.target.value)}
                onBlur={(e) =>
                  handleAddressChange("zipCode", e.target.value.trim())
                }
                placeholder="Zip/Postal Code"
              />
            </div>
          </div>
        </div>
      )}

      {/* Custom Buttons Section */}
      <div className="form-field">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-8">
            Add Buttons
          </h3>
          <p className="text-gray-600 italic">
            Create personalized call-to-action buttons, such as linking to your
            website or booking page, to enhance your profile.
            <br />
            {/* Instructions */}
            <Popover
              trigger="hover"
              aria-label="Custom Action Buttons"
              placement="right"
              className="!z-[9999]"
              content={
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm w-[500px]">
                  <div className="flex items-center mb-2">
                    <svg
                      className="w-5 h-5 text-blue-500 mr-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                      />
                    </svg>
                    <span className="font-semibold text-blue-700">
                      Instructions
                    </span>
                  </div>
                  <p className="text-gray-700 mb-2">
                    <span className="font-medium">Custom Buttons</span>{" "}
                    let you add extra buttons to your digital card for quick
                    actions.
                  </p>
                  <div className="mb-2">
                    <span className="block text-gray-600 font-medium mb-1">
                      Examples:
                    </span>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 pl-2">
                      <li>
                        <span className="font-semibold">Visit My Shop</span>{" "}
                        <span className="text-gray-500">
                          (link to your shop)
                        </span>
                      </li>
                      <li>
                        <span className="font-semibold">
                          Book an Appointment
                        </span>{" "}
                        <span className="text-gray-500">
                          (link to your booking calendar)
                        </span>
                      </li>
                      <li>
                        <span className="font-semibold">
                          Download My Resume
                        </span>{" "}
                        <span className="text-gray-500">
                          (link to a PDF or document)
                        </span>
                      </li>
                      <li>
                        <span className="font-semibold">Contact Me</span>{" "}
                        <span className="text-gray-500">
                          (link to a contact form or email)
                        </span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-gray-700">
                    Each button will appear on your profile and can be
                    customized with your own label and destination link.
                  </p>
                </div>
              }
            >
              <button
                type="button"
                className="flex items-center text-blue-500 hover:underline focus:outline-none"
                tabIndex={0}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                aria-label="Show instructions for custom action buttons"
              >
                <svg
                  className="w-5 h-5 text-blue-500 mr-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                  />
                </svg>
                <span>Instructions</span>
              </button>
            </Popover>
          </p>
        </div>

        {/* Custom Buttons Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
          {formData.customButtons?.map((button) => (
            <div
              key={button.id}
              style={{
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  width: "100%",
                  padding: "1rem",
                  border: "2px solid #007bff",
                  borderRadius: "12px",
                  backgroundColor: "#f0f8ff",
                  transition: "all 0.2s ease",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                    width: "100%",
                    overflow: "hidden"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "0.5rem"
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#007bff",
                        borderRadius: "8px",
                        color: "white",
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        overflow: "hidden"
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "500",
                          fontSize: "14px",
                          color: "#333"
                        }}
                      >
                        Custom Button
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                        }}
                      >
                        Click to edit
                      </div>
                    </div>
                  </div>
                  
                  {/* Button Text Field */}
                  <div style={{ marginTop: "0.5rem", padding: "0.75rem", border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "#f8f9fa" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "500", color: "#333", marginBottom: "0.5rem" }}>
                      Button text
                    </label>
                    <input
                      type="text"
                      value={button.text || ""}
                      onChange={(e) =>
                        handleCustomButtonChange(
                          button.id,
                          "text",
                          e.target.value
                        )
                      }
                      onBlur={(e) =>
                        handleCustomButtonChange(
                          button.id,
                          "text",
                          e.target.value.trim()
                        )
                      }
                      placeholder="e.g., Book Appointment, Download Resume, Contact Me, Visit My Shop"
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>

                  {/* Link Field */}
                  <div style={{ marginTop: "0.5rem", padding: "0.75rem", border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "#f8f9fa" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "500", color: "#333", marginBottom: "0.5rem" }}>
                      Button link
                    </label>
                    <input
                      type="url"
                      value={button.link || ""}
                      onChange={(e) =>
                        handleCustomButtonChange(
                          button.id,
                          "link",
                          e.target.value
                        )
                      }
                      onBlur={(e) =>
                        handleCustomButtonChange(
                          button.id,
                          "link",
                          e.target.value.trim().startsWith("http")
                            ? e.target.value.trim()
                            : e.target.value.trim().length > 4
                              ? `https://${e.target.value.trim()}`
                              : ""
                        )
                      }
                      placeholder="https://example.com"
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Red minus indicator */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeCustomButton(button.id);
                }}
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-8px",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#ff4757",
                  color: "white",
                  border: "2px solid white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "bold",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
                title="Remove button"
              >
                −
              </button>
            </div>
          ))}
        </div>
        {/* We are done with the custom buttons section */}

        {/* Add Button - only show if less than 3 buttons */}
        {(!formData.customButtons || formData.customButtons.length < 3) && (
          <button 
            type="button" 
            className="add-section" 
            onClick={addCustomButton}
            style={{ marginTop: "1rem" }}
          >
            + Add Custom Button
          </button>
        )}
      </div>

      {/* Social Media Section */}
      <div className="form-field">
        <SocialMedia formData={formData} onUpdate={onUpdate} errors={errors} />
      </div>

      {/* Referral section */}
      <>
        <div className="form-field">
          <label>Were you referred to Viiision by someone else?</label>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="wasReferred"
                value="yes"
                checked={formData.wasReferred === true}
                onChange={(e) =>
                  onUpdate("wasReferred", e.target.value === "yes")
                }
              />
              <span>Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="wasReferred"
                value="no"
                checked={formData.wasReferred === false}
                onChange={(e) =>
                  onUpdate("wasReferred", e.target.value === "yes")
                }
              />
              <span>No</span>
            </label>
          </div>
        </div>

        {formData.wasReferred === true && (
          <div className="form-field">
            <label htmlFor="referralName">Who referred you?</label>
            <input
              id="referralName"
              type="text"
              value={formData.referralName || ""}
              onChange={(e) =>
                handleInputChange("referralName", e.target.value)
              }
              onBlur={(e) =>
                handleInputChange("referralName", e.target.value.trim())
              }
              placeholder="Referrer's name"
              className={errors.referralName ? "error" : ""}
            />
            {errors.referralName && (
              <ValidationMessage type="error" message={errors.referralName} />
            )}
          </div>
        )}
      </>
    </div>
  );
};
